import { useState } from 'react';
import { Star, MoreHorizontal, FilePlus2, Save } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { useBreakdownDocuments } from '@/hooks/useBreakdownDocuments';
import { formatCurrency } from '@/lib/utils';
import type { BreakdownDocumentSummary, BreakdownSlotKey } from '@jobos/shared';
import { SaveAsDialog } from './SaveAsDialog';
import { DuplicateDialog } from './DuplicateDialog';
import { SetActiveConfirm } from './SetActiveConfirm';
import { RenameDocumentDialog } from './RenameDocumentDialog';
import { DeleteDocumentConfirm } from './DeleteDocumentConfirm';

export interface BreakdownDocumentListProps {
  jobId: string;
  // Slot to list documents for — scopes the query cache per slot.
  slotKey: BreakdownSlotKey;
  // Called when a row is clicked → wrapper navigates to ?docId=<id>.
  onOpen: (docId: string) => void;
  // Optional override for the "Save as template" button — the breakdown page
  // already has a SaveAsTemplateDialog wired into the editor toolbar, so the
  // page can plug it through. When omitted the button is hidden.
  onSaveAsTemplate?: () => void;
}

function formatLastEdited(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Renders the per-slot list of breakdown documents as a table. The wrapper
// component decides when to show this (vs the editor) based on document
// count and ?docId — see Task 31's resolver.
export function BreakdownDocumentList({
  jobId,
  slotKey,
  onOpen,
  onSaveAsTemplate,
}: BreakdownDocumentListProps) {
  const { data: documents, isLoading } = useBreakdownDocuments(jobId, slotKey);

  const [newOpen, setNewOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<BreakdownDocumentSummary | null>(null);
  const [renameTarget, setRenameTarget] = useState<BreakdownDocumentSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BreakdownDocumentSummary | null>(null);
  const [setActiveTarget, setSetActiveTarget] = useState<BreakdownDocumentSummary | null>(null);

  return (
    <div className="flex flex-col h-full min-h-0 py-4 space-y-4">
      <div className="flex items-center justify-between shrink-0">
        <h2 className="text-lg font-semibold text-gray-900">Versions</h2>
        <div className="flex items-center gap-2">
          {onSaveAsTemplate && (
            <Button variant="outline" size="sm" onClick={onSaveAsTemplate}>
              <Save className="h-4 w-4 mr-1.5" />
              Save as template
            </Button>
          )}
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <FilePlus2 className="h-4 w-4 mr-1.5" />
            New version
          </Button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-md bg-white">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-white">
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="w-32">Tag</TableHead>
              <TableHead className="text-right w-32">Total cost</TableHead>
              <TableHead className="w-48">Last edited</TableHead>
              <TableHead className="w-40">Created by</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-sm text-gray-500">
                  Loading versions...
                </TableCell>
              </TableRow>
            ) : !documents || documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-sm text-gray-500">
                  No versions yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow
                  key={doc.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => onOpen(doc.id)}
                >
                  <TableCell>
                    {doc.isActive ? (
                      <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                    ) : (
                      <span className="block w-4" />
                    )}
                  </TableCell>
                  <TableCell className="font-medium text-gray-900">{doc.name}</TableCell>
                  <TableCell>
                    {doc.tag ? (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                        {doc.tag}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {formatCurrency(doc.totalCost)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatLastEdited(doc.updatedAt)}
                  </TableCell>
                  <TableCell className="text-sm text-gray-600 truncate">
                    {doc.createdByName ?? '—'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-gray-200 text-gray-500"
                          aria-label="Actions"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => onOpen(doc.id)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setRenameTarget(doc)}>
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDuplicateTarget(doc)}>
                          Duplicate
                        </DropdownMenuItem>
                        {!doc.isActive && (
                          <DropdownMenuItem onClick={() => setSetActiveTarget(doc)}>
                            Set as Active
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(doc)}
                          className="text-red-600 focus:text-red-600"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <SaveAsDialog
        jobId={jobId}
        source={null}
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(doc) => onOpen(doc.id)}
      />

      <DuplicateDialog
        jobId={jobId}
        source={duplicateTarget ? { id: duplicateTarget.id, name: duplicateTarget.name } : null}
        open={!!duplicateTarget}
        onOpenChange={(o) => !o && setDuplicateTarget(null)}
      />

      <RenameDocumentDialog
        jobId={jobId}
        document={renameTarget}
        open={!!renameTarget}
        onOpenChange={(o) => !o && setRenameTarget(null)}
      />

      <DeleteDocumentConfirm
        jobId={jobId}
        document={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      />

      <SetActiveConfirm
        jobId={jobId}
        document={setActiveTarget ? { id: setActiveTarget.id, name: setActiveTarget.name } : null}
        open={!!setActiveTarget}
        onOpenChange={(o) => !o && setSetActiveTarget(null)}
      />
    </div>
  );
}
